import { useState, useEffect } from 'react';
import { gql, useMutation, useLazyQuery } from '@apollo/client';
import { observer } from 'mobx-react-lite';
import moment from 'moment';
import { IonIcon, IonText, IonList, IonLoading, IonButton } from '@ionic/react';
import { chevronDownOutline, chevronForwardOutline } from 'ionicons/icons';
import WorkoutInfoContainer from '../WorkoutInfoContainer';
import WorkoutInfoContainerLoader from '../WorkoutInfoContainer/Loader';
import WorkoutAssignmentExercisesLoader from '../WorkoutAssignmentExercisesLoader';
import WorkoutExercise from '../WorkoutExercise';
import SanatizedHtml from 'components/SanatizedHtml';
import { ReactComponent as RestDay } from '../../assets/rest_day.svg';
import { getDayAndRepetitionNo } from 'libs/helpers/helper.lib';

import classes from './style.module.scss';
import workoutAssignmentStore from 'stores/workout-assignment.store';

const GET_WORKOUT_ASSIGNMENT_FIELDS = gql`
  query ($id: ID!) {
    workoutAssignmentById(id: $id) {
      id
      start_date
      end_date
      notes
      workout_repetition_count
      workoutAssignmentCategory {
        id
        name
      }
      workout {
        id
        name
        description
        cover
        workoutTotalDays
      }
    }
  }
`;

const GET_WORKOUT_ASSIGNMENT_WITH_WORKOUT_STATUS = gql`
  query ($id: ID!, $day: Int!, $repetition_no: Int!) {
    workoutAssignmentById(id: $id) {
      id
      start_date
      end_date
      notes
      workout_repetition_count
      workoutAssignmentCategory {
        id
        name
      }
      workout {
        id
        name
        description
        cover
        workoutTotalDays
        workoutExercises(day: $day) {
          id
          day
          repetition_numbers_left
          repetition_numbers_right
          repetition_incremental_left
          repetition_incremental_right
          max_repetition_left
          max_repetition_right
          primary_joint_priority
          primary_plane_motion_priority
          secondary_plane_motion_priority
          proximity_priority
          programming_priority
          motor_control_difficulty
          notes
          exercise {
            id
            name
            description
            video_link
            thumbnail
            primary_joint
            primary_plane_motion
            secondary_plane_motion
            laterality
            equipments {
              id
              name
            }
          }
        }
      }
      workoutAssignmentStatuses(day: $day, repetition_no: $repetition_no) {
        id
        day
        repetition_no
        status
        workoutExercise {
          id
          exercise {
            id
            name
          }
        }
      }
    }
  }
`;

const UPSERT_WORKOUT_ASSIGNMENT_STATUS = gql`
  mutation ($workoutAssignmentStatus: UpsertWorkoutAssignmentStatusInput!) {
    upsertWorkoutAssignmentStatus(
      workoutAssignmentStatus: $workoutAssignmentStatus
    ) {
      id
      workoutAssignment {
        id
        workout {
          id
          name
        }
      }
      workoutExercise {
        id
        exercise {
          id
          name
        }
      }
      day
      repetition_no
      status
      created_at
      updated_at
    }
  }
`;

const WorkoutAssignmentSingle = ({ workoutAssignmentId, summary }) => {
  let selectedDate = workoutAssignmentStore.getSelectedDate();

  if (!selectedDate) {
    selectedDate = moment();
  }

  let day = null;
  let repetition_no = null;

  const [
    getWorkoutAssignmentStatuses,
    {
      called: workoutAssignmentWithStatusCalled,
      loading: workoutAssignmentWithStatusLoading,
      data: workoutAssignmentWithStatus
    }
  ] = useLazyQuery(GET_WORKOUT_ASSIGNMENT_WITH_WORKOUT_STATUS, {
    fetchPolicy: 'no-cache'
  });

  const [
    fetchWorkoutAssignmentBaseData,
    {
      called: workoutAssignmentBaseDataCalled,
      loading: workoutAssignmentBaseDataLoading,
      data: workoutAssignmentBaseData
    }
  ] = useLazyQuery(GET_WORKOUT_ASSIGNMENT_FIELDS, {
    fetchPolicy: 'no-cache'
  });

  if (workoutAssignmentBaseData && selectedDate) {
    const day_repition_no = getDayAndRepetitionNo(
      workoutAssignmentBaseData.workoutAssignmentById,
      selectedDate
    );

    day = day_repition_no.day;
    repetition_no = day_repition_no.repetition_no;
  }

  const [
    upsertStatus,
    { statusData, loading: statusLoading, error: statusError }
  ] = useMutation(UPSERT_WORKOUT_ASSIGNMENT_STATUS);

  const [workoutAssignment, setWorkoutAssignment] = useState();
  const [workoutAssignmentStatuses, setWorkoutAssignmentStatuses] = useState(
    []
  );
  const [showDesc, setShowDesc] = useState(false);
  const [showSummary, setShowSummary] = useState(false);

  useEffect(() => {
    if (workoutAssignmentId) {
      fetchWorkoutAssignmentBaseData({
        variables: {
          id: workoutAssignmentId
        }
      });
    }
  }, [workoutAssignmentId, fetchWorkoutAssignmentBaseData]);

  useEffect(() => {
    if (workoutAssignmentBaseData && day && repetition_no) {
      getWorkoutAssignmentStatuses({
        variables: {
          id: workoutAssignmentBaseData.workoutAssignmentById.id,
          day: day,
          repetition_no: repetition_no
        }
      });
    }
  }, [
    workoutAssignmentBaseData,
    day,
    repetition_no,
    getWorkoutAssignmentStatuses
  ]);

  useEffect(() => {
    if (workoutAssignmentWithStatus) {
      setWorkoutAssignment(workoutAssignmentWithStatus.workoutAssignmentById);
      setWorkoutAssignmentStatuses(
        workoutAssignmentWithStatus.workoutAssignmentById
          .workoutAssignmentStatuses
      );
    }
  }, [workoutAssignmentWithStatus]);

  const getWorkoutStatusPercentage = () => {
    const workoutExercises = workoutAssignment.workout.workoutExercises;
    let completed = 0;
    workoutExercises.forEach((workoutExercise) => {
      const foundWorkoutAssignmentStatus = workoutAssignmentStatuses.find(
        (workoutAssignmentStatus) =>
          workoutAssignmentStatus.day === day &&
          workoutAssignmentStatus.repetition_no === repetition_no &&
          workoutAssignmentStatus.workoutExercise.id === workoutExercise.id
      );

      if (foundWorkoutAssignmentStatus && foundWorkoutAssignmentStatus.status) {
        completed++;
      }
    });

    return completed / workoutExercises.length;
  };

  const handleWorkoutStatusToggle = async (
    workoutAssignmentStatus,
    workoutExercise,
    val
  ) => {
    try {
      const response = await upsertStatus({
        variables: {
          workoutAssignmentStatus: {
            id: workoutAssignmentStatus ? workoutAssignmentStatus.id : null,
            workout_assignment_id: workoutAssignmentId,
            workout_exercise_id: workoutExercise.id,
            day: day,
            repetition_no: repetition_no,
            status: val
          }
        }
      });
      if (response.data.upsertWorkoutAssignmentStatus) {
        const upsertWorkoutAssignmentStatus =
          response.data.upsertWorkoutAssignmentStatus;
        const foundWorkoutAssignmentStatusIndex =
          workoutAssignmentStatuses.findIndex(
            (workoutAssignmentStatus) =>
              workoutAssignmentStatus.id === upsertWorkoutAssignmentStatus.id
          );
        const updatedWorkoutAssignmentStatuses = [...workoutAssignmentStatuses];
        if (foundWorkoutAssignmentStatusIndex > -1) {
          updatedWorkoutAssignmentStatuses[foundWorkoutAssignmentStatusIndex] =
            upsertWorkoutAssignmentStatus;
        } else {
          updatedWorkoutAssignmentStatuses.push(upsertWorkoutAssignmentStatus);
        }
        setWorkoutAssignmentStatuses(updatedWorkoutAssignmentStatuses);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const _renderLoader = () => {
    return (
      <>
        <WorkoutInfoContainerLoader />
        {!summary && <WorkoutAssignmentExercisesLoader />}
      </>
    );
  };

  if (workoutAssignmentWithStatusLoading || workoutAssignmentBaseDataLoading) {
    return _renderLoader();
  }

  if (!workoutAssignment) {
    return _renderLoader();
  }

  return (
    <div className={classes.workoutContainer}>
      <div className={classes.workoutTitleHeadContainer}>
        <WorkoutInfoContainer
          workoutAssignment={workoutAssignment}
          progress={getWorkoutStatusPercentage()}
          summary={summary}
          showSummary={showSummary}
          handleSummaryViewChange={setShowSummary}
        />
      </div>
      {(summary && showSummary) || !summary ? (
        <div
          className={`${classes.workoutDecWorkouts} ${
            !summary && classes.extraPadding
          }`}
        >
          <div
            className={`${classes.workoutDecButton} ion-activatable ripple-parent`}
            onClick={(e) => {
              e.stopPropagation();
              setShowDesc((prevShowDec) => !prevShowDec);
            }}
          >
            <IonText color='light' className='big'>
              Description
            </IonText>
            <IonIcon
              color='light'
              icon={showDesc ? chevronDownOutline : chevronForwardOutline}
            />
          </div>
          {showDesc && (
            <div className={classes.workoutDecDetails}>
              <SanatizedHtml
                dirtyHtml={workoutAssignment.workout.description}
              />
            </div>
          )}
          <div className={classes.workoutDecButtonDivider}></div>
          {workoutAssignment.workout.workoutExercises.length === 0 && (
            <div className={classes.workoutRestDay}>
              <RestDay />
            </div>
          )}
          <div className={classes.workoutExercises}>
            <IonList lines='none'>
              {workoutAssignment.workout.workoutExercises
                ?.slice(
                  0,
                  summary
                    ? 5
                    : workoutAssignment.workout.workoutExercises.length
                )
                ?.map((workoutExercise, index) => {
                  const workoutAssignmentStatus =
                    workoutAssignmentStatuses.find(
                      (workoutAssignmentStatus) => {
                        return (
                          workoutAssignmentStatus.day === day &&
                          workoutAssignmentStatus.repetition_no ===
                            repetition_no &&
                          workoutAssignmentStatus.workoutExercise.id ===
                            workoutExercise.id
                        );
                      }
                    );
                  return (
                    <WorkoutExercise
                      workoutAssignmentId={workoutAssignment.id}
                      workoutExercise={workoutExercise}
                      status={workoutAssignmentStatus?.status}
                      day={day}
                      repetition_no={repetition_no}
                      handleWorkoutStatusToggle={(val) =>
                        handleWorkoutStatusToggle(
                          workoutAssignmentStatus,
                          workoutExercise,
                          val
                        )
                      }
                      key={`${workoutAssignmentId}-${workoutExercise.id}`}
                    />
                  );
                })}
            </IonList>
            {summary && (
              <div className={classes.viewMoreContainer}>
                <div className={classes.workoutDecButtonDivider}></div>
                <IonButton fill='clear' color='secondary'>
                  {' '}
                  View All
                </IonButton>
              </div>
            )}
          </div>
        </div>
      ) : (
        ''
      )}

      <IonLoading isOpen={statusLoading} />
    </div>
  );
};

export default observer(WorkoutAssignmentSingle);
